import { CondensedButton, Flex, Heading, IconAddLine } from "@instructure/ui";
import { useTranslation } from "react-i18next";
import React, { useContext, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { ActionService } from "../../services/ActionService";
import { ExternalService } from "../../services/ExternalService";
import { ReflectionService } from "../../services/ReflectionService";
import { UserDetailContext } from "../../utils/Context";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import ReflectionEntry from "./ReflectionEntry";
import ReflectionModal from "./ReflectionModal";
import {
  upsertReflection,
  upsertReflectionDraft,
  upsertReflectionDraftFromTemplate,
  upsertReflectionFromTemplate,
} from "./utils";

export const ContextReflections = ({
  contextId,
  contextName,
  externalUserName,
  reflectionTemplates,
  sectionRefTemplate,
}) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const userDetails = useContext(UserDetailContext);
  const [openReflectionModal, setOpenReflectionModal] = useState(false);
  const [reflectionList, setReflectionList] = useState([]);
  const [loadReflections, setLoadReflections] = useState(true);
  const [actions, setActions] = useState(null);

  useEffect(() => {
    async function fetchReflectionsAndActions() {
      try {
        setReflectionList([]);
        setLoadReflections(true);

        const getActions = externalUserName
          ? ExternalService.getExternalActionsForContext
          : ActionService.getActionsForContext;

        const getReflections = externalUserName
          ? ExternalService.getExternalReflectionsForContext
          : ReflectionService.getReflectionsForContext;

        const [reflections, actions] = await Promise.all([
          getReflections(contextId, userDetails.canvas_id),
          getActions(contextId),
        ]);

        setActions(actions);

        const filteredReflections = [];
        for (const reflection of reflections) {
          if (reflection.from_template) {
            if (
              !filteredReflections.some((r) => r.ddl_id === reflection.ddl_id)
            ) {
              const template = reflectionTemplates.find(
                (t) => t.id === reflection.template_id
              );
              const groupedReflections = reflections.filter(
                (r) => r.ddl_id === reflection.ddl_id
              );

              const templateReflection = {
                ...reflection,
                sections: template.sections.map((section, i) => ({
                  id: section.id,
                  content: groupedReflections.find(
                    (r) => r.section_id === section.id
                  ).content,
                  header_question: section.header_question,
                  helper_text: template.sections[i].helper_text,
                })),
              };
              filteredReflections.push(templateReflection);
            }
          } else {
            filteredReflections.push(reflection);
          }
        }

        filteredReflections.sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
        );
        setReflectionList(filteredReflections);
      } catch {
        showNotification(
          t("notifications.general.fetchError", {
            obj: t("reflections.reflections"),
          }),
          ERROR
        );
      } finally {
        setLoadReflections(false);
      }
    }
    fetchReflectionsAndActions();
  }, [
    contextId,
    showNotification,
    externalUserName,
    userDetails.canvas_id,
    reflectionTemplates,
  ]);

  async function handleAddReflection(reflectionText) {
    try {
      const newReflection = await upsertReflection({
        reflectionText,
        contextId,
        author: externalUserName ? externalUserName : userDetails.name,
        externalUserName: !!externalUserName,
      });
      setReflectionList((prevList) => [newReflection, ...prevList]);
      setOpenReflectionModal(false);
      showNotification(
        t("notifications.general.createSuccess", {
          obj: t("reflections.reflection"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.createError", {
          obj: t("reflections.reflection"),
        }),
        ERROR
      );
    }
  }

  async function handleAddReflectionFromTemplate(templateId, sections) {
    try {
      const newReflection = await upsertReflectionFromTemplate({
        sections,
        author: externalUserName ? externalUserName : userDetails.name,
        contextId,
        templateId,
        externalUserName: !!externalUserName,
      });
      setReflectionList((prevList) => [newReflection, ...prevList]);
      setOpenReflectionModal(false);
      showNotification(
        t("notifications.general.createSuccess", {
          obj: t("reflections.reflection"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.createError", {
          obj: t("reflections.reflection"),
        }),
        ERROR
      );
    }
  }

  async function handleSaveReflectionDraft(reflectionText) {
    try {
      const newReflection = await upsertReflectionDraft({
        reflectionText,
        author: userDetails.name,
        authorId: userDetails.canvas_id,
        contextId,
      });
      setReflectionList((prevList) => [newReflection, ...prevList]);
      setOpenReflectionModal(false);
      showNotification(
        t("notifications.general.createSuccess", {
          obj: t("reflections.reflectionDraft"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.createError", {
          obj: t("reflections.reflectionDraft"),
        }),
        ERROR
      );
    }
  }

  async function handleSaveReflectionDraftFromTemplate(templateId, sections) {
    try {
      const newReflection = await upsertReflectionDraftFromTemplate({
        sections,
        author: userDetails.name,
        authorId: userDetails.canvas_id,
        contextId,
        templateId,
      });
      setReflectionList((prevList) => [newReflection, ...prevList]);
      setOpenReflectionModal(false);
      showNotification(
        t("notifications.general.createSuccess", {
          obj: t("reflections.reflectionDraft"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.createError", {
          obj: t("reflections.reflectionDraft"),
        }),
        ERROR
      );
    }
  }

  return (
    <>
      <Flex alignItems="center" margin="xx-small 0 small 0" gap="small">
        <Flex.Item>
          <Heading as="h3" level="h4">
            {t("reflections.reflections")}
          </Heading>
        </Flex.Item>
        <Flex.Item>
          <CondensedButton
            size="small"
            renderIcon={IconAddLine}
            onClick={() => setOpenReflectionModal(true)}
            interaction={loadReflections ? "disabled" : "enabled"}
          >
            {t("reflections.addReflection")}
          </CondensedButton>
        </Flex.Item>
      </Flex>
      {loadReflections ? (
        <Skeleton
          count={3}
          style={{ height: 64, borderRadius: 5, marginBottom: 10 }}
        />
      ) : reflectionList.length === 0 ? (
        <Heading level="h5" as="h4">
          {t("reflections.noReflections")}
        </Heading>
      ) : (
        <Flex wrap="wrap" gap="small">
          {reflectionList.map((ref) => (
            <Flex.Item key={ref.ddl_id} size="100%">
              <ReflectionEntry
                reflection={ref}
                contextName={contextName}
                contextId={contextId}
                externalUserName={externalUserName}
                reflectionList={reflectionList}
                setReflectionList={setReflectionList}
                actions={actions}
                setActions={setActions}
                reflectionTemplates={reflectionTemplates}
                sectionRefTemplate={sectionRefTemplate}
              />
            </Flex.Item>
          ))}
        </Flex>
      )}
      <ReflectionModal
        openModal={openReflectionModal}
        setOpenModal={setOpenReflectionModal}
        handleSubmit={handleAddReflection}
        handleSubmitFromTemplate={handleAddReflectionFromTemplate}
        onDraftChange={handleSaveReflectionDraft}
        onDraftChangeFromTemplate={handleSaveReflectionDraftFromTemplate}
        contextName={contextName}
        draftEnabled={!externalUserName}
        sectionRefTemplate={sectionRefTemplate}
      />
    </>
  );
};
